import { IBillingProvider, PurchaseOptions } from './IBillingProvider';
import type {
  BillingProduct,
  PurchaseResult,
  Subscription,
  RestoredPurchase,
  BillingEventListener,
  BillingEvent,
  SubscriptionStatus,
} from '../types';
import { featureFlags } from '@/config/feature-flags';
import { GOOGLE_PLAY_VERIFIED_PRODUCT_ID } from '@/config/subscriptions';
import { Capacitor } from '@capacitor/core';
import BillingDiagnostics from '@/plugins/BillingDiagnostics';
import GooglePlayBilling, { type GooglePlayPurchase, type PurchaseUpdateEvent } from '@/plugins/GooglePlayBilling';
import { invokeAuthedEdge, InvokeNotAuthenticatedError } from '@/lib/supabase/invokeAuthedEdge';
import { toast } from 'sonner';

/**
 * V287: Google Play Billing Provider (Capacitor Native)
 *
 * Uses native Capacitor plugin instead of react-native-iap.
 * react-native-iap doesn't work in Capacitor builds because Capacitor
 * builds web bundles, not React Native bundles.
 *
 * CRITICAL RULES:
 * - NEVER acknowledge purchases client-side
 * - ALWAYS await server verification before acknowledging
 * - Product ID: verified_monthly (must match Play Console)
 */
export class GoogleBillingProvider implements IBillingProvider {
  readonly platform = 'google' as const;
  private initialized = false;
  private eventListeners: Set<BillingEventListener> = new Set();
  private purchaseUpdateSubscription: any = null;
  private purchaseErrorSubscription: any = null;
  // V288.2: Processing guard to prevent duplicate verification
  private processingToken: string | null = null;

  async initialize(): Promise<void> {
    console.log('🚀 V287.1: [GoogleBilling] initialize() called - TypeScript side');
    console.log('🔧 V282.3: [GoogleBilling] Initializing...');

    // ========================================================================
    // CHECK KILL SWITCH FIRST
    // ========================================================================
    const enabled = await featureFlags.isAndroidBillingEnabled();

    if (!enabled) {
      const message =
        'Android billing is temporarily unavailable. ' +
        'Please try again later or use the web version at BlissNet.app.';

      console.warn('⛔ V282.3: [GoogleBilling] Android billing disabled via kill switch');
      console.warn('⛔', message);

      throw new Error(message);
    }

    // ========================================================================
    // CONTINUE WITH NORMAL INITIALIZATION
    // ========================================================================
    try {
      // V287: Initialize connection to Google Play using Capacitor plugin
      console.log('🔍 V287: [GoogleBilling] Calling GooglePlayBilling.initConnection()...');
      const result = await GooglePlayBilling.initConnection();
      console.log('✅ V287: [GoogleBilling] initConnection() result:', result);
      console.log('✅ V287: [GoogleBilling] Connection initialized successfully');

      // Set up purchase listeners
      this.setupPurchaseListeners();

      this.initialized = true;
      console.log('✅ V287: [GoogleBilling] Initialization complete');
    } catch (error: any) {
      console.error('❌ V287: [GoogleBilling] Initialization failed:', error);
      console.error('❌ V287: [GoogleBilling] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      throw new Error(`Google Play Billing initialization failed: ${error.message}`);
    }
  }

  isReady(): boolean {
    return this.initialized;
  }

  async getProducts(): Promise<BillingProduct[]> {
    console.log('🚀 V282.3: [GoogleBilling] Fetching products...');
    console.log(`🚀 V282.3: [GoogleBilling] Querying skus: ["${GOOGLE_PLAY_VERIFIED_PRODUCT_ID}"]`);

    if (!this.initialized) {
      throw new Error('GoogleBillingProvider not initialized');
    }

    // ========================================================================
    // V282.3: DIAGNOSTIC PREFLIGHT - Identify environmental issues
    // ========================================================================
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      try {
        console.log('🔍 V282.3: [GoogleBilling] Running diagnostic preflight...');
        const diagnostics = await BillingDiagnostics.getAndroidDiagnostics();

        console.log('📊 V282.3: [GoogleBilling] Android Diagnostics:', {
          packageName: diagnostics.packageName,
          installerPackage: diagnostics.installerPackage,
          playServicesAvailable: diagnostics.playServicesAvailable,
          deviceApiLevel: diagnostics.deviceApiLevel,
        });

        // CRITICAL CHECK 1: App must be installed from Google Play Store
        if (diagnostics.installerPackage !== 'com.android.vending') {
          const errorMsg =
            `❌ Billing catalog unavailable: App is NOT installed from Google Play Store.\n` +
            `\n` +
            `📦 INSTALLER DETAILS:\n` +
            `   Current installer: ${diagnostics.installerPackage}\n` +
            `   Required installer: com.android.vending (Google Play Store)\n` +
            `\n` +
            `💡 FIX:\n` +
            `   1. Uninstall the app completely\n` +
            `   2. Install from Google Play Internal Testing link\n` +
            `   3. DO NOT install via 'npm run android' or APK file\n` +
            `\n` +
            `ℹ️ Google Play Billing ONLY works when installed from Play Store.`;

          console.error('❌ V282.3: [GoogleBilling]', errorMsg);
          throw new Error(errorMsg);
        }

        // CRITICAL CHECK 2: Google Play Services must be available
        if (!diagnostics.playServicesAvailable) {
          const errorMsg =
            `❌ Google Play Services unavailable – cannot query billing.\n` +
            `\n` +
            `💡 FIX:\n` +
            `   1. Update Google Play Services on device\n` +
            `   2. Ensure device has Google Play Store installed\n` +
            `   3. Try restarting the device`;

          console.error('❌ V282.3: [GoogleBilling]', errorMsg);
          throw new Error(errorMsg);
        }

        console.log('✅ V282.3: [GoogleBilling] Diagnostic preflight passed');
      } catch (error: any) {
        // If error is already our diagnostic error, re-throw it
        if (error.message.includes('Billing catalog unavailable') ||
            error.message.includes('Google Play Services unavailable')) {
          throw error;
        }
        // Otherwise, log diagnostic failure but continue (don't block on diagnostic errors)
        console.warn('⚠️ V282.3: [GoogleBilling] Diagnostic preflight failed (non-blocking):', error);
      }
    }

    // ========================================================================
    // V287: QUERY PRODUCTS using Capacitor plugin
    // ========================================================================
    try {
      console.log('🔍 V287: [GoogleBilling] Querying Google Play catalog...');

      // Fetch subscription products from Google Play using Capacitor plugin
      const result = await GooglePlayBilling.getSubscriptions({
        skus: [GOOGLE_PLAY_VERIFIED_PRODUCT_ID],
      });

      const products = result.products || [];
      console.log(`✅ V287: [GoogleBilling] Products fetched: ${products.length}`);

      if (products.length === 0) {
        const errorMsg =
          `No products returned from Google Play.\n` +
          `\n` +
          `💡 TROUBLESHOOTING:\n` +
          `1. App MUST be installed from Google Play (Internal Testing track)\n` +
          `2. Product '${GOOGLE_PLAY_VERIFIED_PRODUCT_ID}' must be Active in Play Console\n` +
          `3. Device must use a License Tester Gmail account\n` +
          `4. Check Play Console > Setup > License testing\n` +
          `5. Verify product has base plan 'monthly' configured`;

        console.error('❌ V287: [GoogleBilling]', errorMsg);
        throw new Error(errorMsg);
      }

      console.log(`✅ V287: [GoogleBilling] Returned productIds: [${products.map(p => `"${p.productId}"`).join(', ')}]`);

      return products.map((product) => {
        console.log(`✅ V287: [GoogleBilling] Product ${product.productId} has offerToken:`, product.offerToken ? 'present' : 'missing');

        return {
          id: product.productId,
          platform: 'google' as const,
          price: product.price,
          priceValue: product.priceAmountMicros / 1000000, // Convert micros to currency units
          currency: product.priceCurrencyCode,
          title: product.title || 'BlissNet Verified 🌿',
          description: product.description || 'Get verified status with +100 Trust Score',
          recurring: true,
          billingPeriod: 'monthly' as const,
          offerToken: product.offerToken, // CRITICAL: Required for purchase flow
        };
      });
    } catch (error: any) {
      console.error('❌ V287: [GoogleBilling] Failed to fetch products:', error);
      console.error('❌ V287: [GoogleBilling] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      throw error; // Re-throw to preserve detailed error message
    }
  }

  async launchPurchaseFlow(productId: string, options?: PurchaseOptions): Promise<PurchaseResult> {
    console.log('🛒 V282.2: [GoogleBilling] Launching purchase flow for:', productId);
    console.log('🛒 V282.2: [GoogleBilling] Purchase options:', {
      offerToken: options?.offerToken ? 'present' : 'missing'
    });

    // Kill switch check (defense in depth)
    const enabled = await featureFlags.isAndroidBillingEnabled();
    if (!enabled) {
      console.warn('⛔ V282.2: [GoogleBilling] Purchase blocked by kill switch');
      return {
        success: false,
        error: {
          code: 'BILLING_DISABLED',
          message: 'Android billing is temporarily unavailable. Please try again later or use the web version at BlissNet.app.',
          recoverable: false,
        },
      };
    }

    if (!this.initialized) {
      return {
        success: false,
        error: {
          code: 'NOT_INITIALIZED',
          message: 'Billing provider not initialized',
          recoverable: true,
        },
      };
    }

    // Emit purchase started event
    this.emitEvent({
      type: 'purchase_started',
      platform: 'google',
      timestamp: new Date(),
      data: { productId, offerToken: options?.offerToken },
    });

    try {
      // V287: Validate offerToken is provided (REQUIRED for Google Play subscriptions)
      if (!options?.offerToken) {
        console.error('❌ V287: [GoogleBilling] No offerToken provided - cannot purchase subscription');
        throw new Error('offerToken is required for subscription purchases');
      }

      console.log('✅ V287: [GoogleBilling] Launching billing flow with offerToken');

      // V300: Get current user ID for Identity-Aware Billing
      let obfuscatedAccountId: string | undefined;
      let obfuscatedProfileId: string | undefined;

      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: { user } } = await supabase.auth.getUser();

        if (user?.id) {
          obfuscatedAccountId = user.id;  // V300: BlissNet UUID (non-PII)
          obfuscatedProfileId = user.id;  // V300: Use same UUID for profile
          console.log('✅ V300: [GoogleBilling] Identity-Aware Billing enabled');
          console.log('V300: obfuscatedAccountId set (length:', obfuscatedAccountId.length, ')');
        } else {
          console.warn('⚠️ V300: [GoogleBilling] No user ID - proceeding without identity binding');
        }
      } catch (authError) {
        console.error('❌ V300: [GoogleBilling] Failed to get user ID:', authError);
        console.warn('⚠️ V300: [GoogleBilling] Proceeding without identity binding');
      }

      // V300: Launch billing flow with Identity-Aware Billing parameters
      const purchaseResult = await GooglePlayBilling.requestSubscription({
        productId,
        offerToken: options.offerToken,
        obfuscatedAccountId,  // V300: BlissNet user UUID
        obfuscatedProfileId,  // V300: Profile UUID
      });

      console.log('V287.4: [GoogleBilling] Purchase result received:', JSON.stringify(purchaseResult));

      // V287.4: Validate response format
      if (typeof purchaseResult !== 'object' || purchaseResult === null) {
        console.error('V287.4: [GoogleBilling] Invalid result format - not an object');
        throw new Error('Invalid purchase result format');
      }

      // V287.4: Handle success case
      if (purchaseResult.success === true) {
        // Validate required fields
        if (!purchaseResult.purchaseToken) {
          console.error('V287.4: [GoogleBilling] Success response missing purchaseToken');
          throw new Error('Purchase succeeded but token is missing');
        }

        console.log('V287.4: ✅ Purchase successful, token:', purchaseResult.purchaseToken);
        console.log('V287.4: isRestore:', purchaseResult.isRestore);

        // V210.9: Dispatch custom event for PurchaseSuccessHandler
        // This triggers immediate manual sync BEFORE polling starts
        console.log('💰 V210.9: Dispatching blissnet_purchase_success event');
        window.dispatchEvent(new CustomEvent('blissnet_purchase_success', {
          detail: {
            purchaseToken: purchaseResult.purchaseToken,
            productId,
            packageName: 'com.blissnet.app'
          }
        }));

        // Emit purchase completed event
        this.emitEvent({
          type: 'purchase_completed',
          platform: 'google',
          timestamp: new Date(),
          data: {
            productId,
            purchaseToken: purchaseResult.purchaseToken,
            isRestore: purchaseResult.isRestore
          },
        });

        // V287.4: Trigger server verification and polling
        await this.handlePurchaseSuccess(purchaseResult.purchaseToken, purchaseResult.isRestore || false);

        return {
          success: true,
          productId,
          purchaseToken: purchaseResult.purchaseToken,
        };

      } else if (purchaseResult.success === false) {
        // V287.4: Handle error case
        const errorCode = purchaseResult.errorCode || 'UNKNOWN_ERROR';
        const errorMessage = purchaseResult.errorMessage || 'Purchase failed';

        console.error('V287.4: [GoogleBilling] Purchase failed -', errorCode, errorMessage);

        // Don't emit event for user cancellation (not an error)
        if (errorCode !== 'USER_CANCELED') {
          this.emitEvent({
            type: 'purchase_failed',
            platform: 'google',
            timestamp: new Date(),
            data: { productId, errorCode, errorMessage },
          });
        }

        return {
          success: false,
          error: {
            code: errorCode,
            message: errorMessage,
            recoverable: errorCode !== 'USER_CANCELED',
          },
        };

      } else {
        // V287.4: Handle unexpected response format
        console.error('V287.4: [GoogleBilling] Unexpected response format:', purchaseResult);
        throw new Error('Unexpected purchase result format');
      }

    } catch (error: any) {
      console.error('❌ V287.4: [GoogleBilling] Purchase exception:', error);

      // Emit purchase failed event
      this.emitEvent({
        type: 'purchase_failed',
        platform: 'google',
        timestamp: new Date(),
        data: { productId, error: error.message },
      });

      return {
        success: false,
        error: {
          code: error.code || 'PURCHASE_FAILED',
          message: this.getUserFriendlyErrorMessage(error),
          recoverable: this.isRecoverableError(error),
          details: error,
        },
      };
    }
  }

  async getActiveSubscriptions(): Promise<Subscription[]> {
    console.log('🚀 V287: [GoogleBilling] Fetching active subscriptions...');

    if (!this.initialized) {
      throw new Error('GoogleBillingProvider not initialized');
    }

    try {
      // Get available purchases using Capacitor plugin
      const result = await GooglePlayBilling.getAvailablePurchases();
      const purchases = result.purchases || [];
      console.log('✅ V287: [GoogleBilling] Available purchases:', purchases.length);

      // Map to subscription objects
      const subscriptions = purchases
        .filter((purchase) => this.isSubscriptionPurchase(purchase))
        .map((purchase) => this.mapPurchaseToSubscription(purchase));

      return subscriptions;
    } catch (error: any) {
      console.error('❌ V287: [GoogleBilling] Failed to fetch subscriptions:', error);
      return [];
    }
  }

  async restorePurchases(): Promise<RestoredPurchase[]> {
    console.log('🚀 V287: [GoogleBilling] Restoring purchases...');

    if (!this.initialized) {
      throw new Error('GoogleBillingProvider not initialized');
    }

    // Emit restore started event
    this.emitEvent({
      type: 'restore_started',
      platform: 'google',
      timestamp: new Date(),
    });

    try {
      const result = await GooglePlayBilling.getAvailablePurchases();
      const purchases = result.purchases || [];
      console.log('✅ V287: [GoogleBilling] Restored purchases:', purchases.length);

      // Emit restore completed event
      this.emitEvent({
        type: 'restore_completed',
        platform: 'google',
        timestamp: new Date(),
        data: { count: purchases.length },
      });

      return purchases.map((purchase) => ({
        productId: purchase.productId,
        purchaseToken: purchase.purchaseToken || '',
        transactionId: purchase.orderId,
        transactionDate: new Date(purchase.purchaseTime || Date.now()),
        platform: 'google' as const,
      }));
    } catch (error: any) {
      console.error('❌ V287: [GoogleBilling] Failed to restore purchases:', error);
      return [];
    }
  }

  addEventListener(listener: BillingEventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  dispose(): void {
    console.log('🚀 V287: [GoogleBilling] Disposing...');

    // Remove purchase listeners
    if (this.purchaseUpdateSubscription) {
      GooglePlayBilling.removeAllListeners();
      this.purchaseUpdateSubscription = null;
    }

    // Clear event listeners
    this.eventListeners.clear();

    // End connection
    GooglePlayBilling.endConnection();

    this.initialized = false;
    console.log('✅ V287: [GoogleBilling] Disposed');
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * V287.4: Handle successful purchase - verify and poll
   * V288.2: Added processing guard to prevent duplicate verification
   */
  private async handlePurchaseSuccess(purchaseToken: string, isRestore: boolean): Promise<void> {
    console.log('V287.4: [GoogleBilling] handlePurchaseSuccess - token:', purchaseToken, 'isRestore:', isRestore);

    // V288.2: Guard against duplicate processing
    if (this.processingToken === purchaseToken) {
      console.log('⚠️ V288.2: [GoogleBilling] Already processing this token, skipping duplicate');
      return;
    }

    this.processingToken = purchaseToken;

    try {
      console.log('🔐 V287.4: Calling server verification via invokeAuthedEdge...');

      let data: { verified?: boolean; userId?: string; trustScoreDelta?: number; expiresAt?: string } | null = null;
      let error: { code?: string; message?: string; requestId?: string } | null = null;
      let requestId: string | undefined;
      try {
        const result = await invokeAuthedEdge<{ verified?: boolean; userId?: string; trustScoreDelta?: number; expiresAt?: string }>(
          'google-verify-purchase',
          {
            purchaseToken,
            productId: 'verified_monthly',
            packageName: 'com.blissnet.app',
          },
          { route: 'google_billing_handle' }
        );
        data = result.data;
        error = result.error;
        requestId = result.requestId;
      } catch (invokeErr) {
        if (invokeErr instanceof InvokeNotAuthenticatedError) {
          toast.error('Session expired. Please sign in again and retry.', {
            description: invokeErr.requestId ? `Request ID: ${invokeErr.requestId}` : undefined,
            duration: 4000,
          });
          console.error('[GoogleBilling] google-verify-purchase not authenticated requestId=%s', invokeErr.requestId);
          return;
        }
        throw invokeErr;
      }

      if (error || (data && (data as { ok?: boolean }).ok === false) || !data?.verified) {
        const msg = error?.message ?? (data && (data as { error?: string }).error) ?? 'Verification failed';
        const rid = error?.requestId ?? requestId;
        console.error('[GoogleBilling] google-verify-purchase failed', { error, data, requestId: rid });
        toast.error(msg, { description: rid ? `Request ID: ${rid}` : undefined, duration: 5000 });
        throw new Error(msg);
      }

      console.log('✅ V287.4: Server verification successful! requestId=%s', requestId);
      console.log('📊 V287.4: Trust score delta:', data.trustScoreDelta);

      // CRITICAL: Only acknowledge purchase after server confirms
      console.log('🏁 V287.4: Acknowledging purchase...');
      await GooglePlayBilling.acknowledgePurchase({ purchaseToken });
      console.log('✅ V287.4: Purchase acknowledged successfully');

      // V287.3: Poll for verification to complete (mirrors Paddle flow)
      const userId = data.userId;
      if (userId) {
        console.log('🔄 V287.4: [GoogleBilling] Polling for verification completion...');
        const verified = await this.pollForVerification(userId);

        if (verified) {
          console.log('✅ V287.4: [GoogleBilling] Verification polling succeeded');
        } else {
          console.warn('⚠️ V287.4: [GoogleBilling] Verification polling timed out (non-fatal)');
        }
      }

      // Emit subscription activated event
      this.emitEvent({
        type: 'subscription_activated',
        platform: 'google',
        timestamp: new Date(),
        data: {
          productId: GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
          verified: true,
          isRestore,
          trustScoreDelta: data.trustScoreDelta,
          expiresAt: data.expiresAt,
        },
      });

    } catch (error: any) {
      console.error('❌ V287.4: Purchase verification failed:', error);

      // Emit purchase failed event
      this.emitEvent({
        type: 'purchase_failed',
        platform: 'google',
        timestamp: new Date(),
        data: {
          productId: GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
          error: error.message || 'Verification failed',
        },
      });

      throw error; // Re-throw to propagate to caller
    } finally {
      // V288.2: Clear processing guard
      this.processingToken = null;
    }
  }

  /**
   * V287.3: Poll for verification status (mirrors Paddle polling logic)
   * Reused from VerifyPaywall.tsx lines 247-294
   */
  private async pollForVerification(userId: string): Promise<boolean> {
    console.log('🚀 V287.3: [GoogleBilling] Starting verification polling...');

    const maxAttempts = 15;
    const baseDelay = 1500;
    const maxDelay = 5000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔄 V287.3: [GoogleBilling] Poll attempt ${attempt}/${maxAttempts}`);

      try {
        // Wait before checking (exponential backoff)
        const delay = Math.min(baseDelay * attempt, maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));

        // Check database directly for verification status
        const { supabase } = await import('@/integrations/supabase/client');

        const { data: freshProfile } = await supabase
          .from('users')
          .select('is_verified, trust_score, verified_at')
          .eq('id', userId)
          .single();

        console.log('🔍 V287.3: [GoogleBilling] Fresh profile data:', {
          is_verified: freshProfile?.is_verified,
          trust_score: freshProfile?.trust_score,
          verified_at: freshProfile?.verified_at
        });

        if (freshProfile?.is_verified === true) {
          console.log('✅ V287.3: [GoogleBilling] Verification detected!');
          return true;
        }

      } catch (error) {
        console.error(`❌ V287.3: [GoogleBilling] Poll attempt ${attempt} failed:`, error);
        // Continue polling even on error
      }
    }

    console.warn('⚠️ V287.3: [GoogleBilling] Verification polling timed out');
    return false;
  }

  /**
   * Set up purchase update listener
   * CRITICAL: Do NOT acknowledge purchases here - wait for server verification
   */
  private setupPurchaseListeners(): void {
    console.log('🚀 V287: [GoogleBilling] Setting up purchase listeners...');

    // Purchase update listener using Capacitor plugin
    this.purchaseUpdateSubscription = GooglePlayBilling.addListener(
      'purchaseUpdate',
      async (event: PurchaseUpdateEvent) => {
        console.log('🚀 V287: [GoogleBilling] Purchase update received:', event.type);

        if (event.type === 'purchase_updated' && event.purchase) {
          const purchase = event.purchase;
          console.log('📦 V287: Purchase token:', purchase.purchaseToken);

          // V288.2: Guard against duplicate processing
          if (this.processingToken === purchase.purchaseToken) {
            console.log('⚠️ V288.2: [GoogleBilling] Already processing this token in listener, skipping');
            return;
          }

          try {
            console.log('🔐 V287: Calling server verification via invokeAuthedEdge...');

            let data: { verified?: boolean; userId?: string; trustScoreDelta?: number; expiresAt?: string } | null = null;
            let error: { code?: string; message?: string; requestId?: string } | null = null;
            let requestId: string | undefined;
            try {
              const result = await invokeAuthedEdge<{ verified?: boolean; userId?: string; trustScoreDelta?: number; expiresAt?: string }>(
                'google-verify-purchase',
                {
                  purchaseToken: purchase.purchaseToken,
                  productId: 'verified_monthly',
                  packageName: 'com.blissnet.app',
                },
                { route: 'google_billing_listener' }
              );
              data = result.data;
              error = result.error;
              requestId = result.requestId;
            } catch (invokeErr) {
              if (invokeErr instanceof InvokeNotAuthenticatedError) {
                toast.error('Session expired. Please sign in again and retry.', {
                  description: invokeErr.requestId ? `Request ID: ${invokeErr.requestId}` : undefined,
                  duration: 4000,
                });
                console.error('[GoogleBilling] google-verify-purchase not authenticated requestId=%s', invokeErr.requestId);
                return;
              }
              throw invokeErr;
            }

            if (error || (data && (data as { ok?: boolean }).ok === false) || !data?.verified) {
              const msg = error?.message ?? (data && (data as { error?: string }).error) ?? 'Verification failed';
              const rid = error?.requestId ?? requestId;
              console.error('[GoogleBilling] google-verify-purchase failed', { error, data, requestId: rid });
              toast.error(msg, { description: rid ? `Request ID: ${rid}` : undefined, duration: 5000 });
              throw new Error(msg);
            }

            console.log('✅ V287: Server verification successful! requestId=%s', requestId);
            console.log('📊 V287: Trust score delta:', data.trustScoreDelta);

            // CRITICAL: Only acknowledge purchase after server confirms
            console.log('🏁 V287: Acknowledging purchase...');
            await GooglePlayBilling.acknowledgePurchase({
              purchaseToken: purchase.purchaseToken,
            });

            console.log('✅ V287: Purchase acknowledged successfully');

            // V287.3: Poll for verification to complete (mirrors Paddle flow)
            // This ensures is_verified=true before showing success UX
            const userId = data.userId; // Returned from google-verify-purchase
            if (userId) {
              console.log('🔄 V287.3: [GoogleBilling] Polling for verification completion...');
              const verified = await this.pollForVerification(userId);

              if (verified) {
                console.log('✅ V287.3: [GoogleBilling] Verification polling succeeded');
              } else {
                console.warn('⚠️ V287.3: [GoogleBilling] Verification polling timed out (non-fatal)');
              }
            }

            // Emit subscription activated event
            this.emitEvent({
              type: 'subscription_activated',
              platform: 'google',
              timestamp: new Date(),
              data: {
                productId: purchase.productId,
                transactionId: purchase.orderId,
                verified: true,
                trustScoreDelta: data.trustScoreDelta,
                expiresAt: data.expiresAt,
              },
            });

          } catch (error: any) {
            console.error('❌ V287: Purchase verification failed:', error);

            // Emit purchase failed event
            this.emitEvent({
              type: 'purchase_failed',
              platform: 'google',
              timestamp: new Date(),
              data: {
                productId: purchase.productId,
                error: error.message || 'Verification failed',
              },
            });

            // DO NOT acknowledge purchase on error
            console.log('⚠️ V287: Purchase NOT acknowledged due to verification failure');
          }
        } else if (event.type === 'purchase_canceled') {
          console.log('⚠️ V287: User canceled purchase');

          this.emitEvent({
            type: 'purchase_failed',
            platform: 'google',
            timestamp: new Date(),
            data: {
              error: 'User canceled purchase',
              code: 'USER_CANCELED',
            },
          });
        } else if (event.type === 'purchase_error') {
          console.error('❌ V287: Purchase error:', event.error);

          this.emitEvent({
            type: 'purchase_failed',
            platform: 'google',
            timestamp: new Date(),
            data: {
              error: event.error || 'Unknown error',
              code: event.code,
            },
          });
        }
      }
    );

    console.log('✅ V287: [GoogleBilling] Purchase listeners set up');
  }

  /**
   * Emit billing event to all listeners
   */
  private emitEvent(event: BillingEvent): void {
    this.eventListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('❌ V287: [GoogleBilling] Event listener error:', error);
      }
    });
  }

  /**
   * Check if purchase is a subscription
   */
  private isSubscriptionPurchase(purchase: GooglePlayPurchase): boolean {
    // Check if product ID matches our subscription products
    return purchase.productId === GOOGLE_PLAY_VERIFIED_PRODUCT_ID;
  }

  /**
   * Map Google Play purchase to our Subscription type
   */
  private mapPurchaseToSubscription(purchase: GooglePlayPurchase): Subscription {
    return {
      id: purchase.orderId || purchase.purchaseToken || '',
      productId: purchase.productId,
      status: this.mapPurchaseStateToStatus(purchase),
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // Default 30 days
      startDate: new Date(purchase.purchaseTime || Date.now()),
      autoRenewing: true, // Assume auto-renewing for active subscriptions
      platform: 'google' as const,
      metadata: {
        purchaseToken: purchase.purchaseToken,
        orderId: purchase.orderId,
      },
    };
  }

  /**
   * Map Google Play purchase state to our SubscriptionStatus
   */
  private mapPurchaseStateToStatus(purchase: GooglePlayPurchase): SubscriptionStatus {
    // Google Play purchase states:
    // 0: Purchased
    // 1: Canceled
    // 2: Pending

    const purchaseState = purchase.purchaseState;

    if (purchaseState === 0) {
      // Check if acknowledged
      if (purchase.acknowledged) {
        return 'active';
      }
      return 'pending';
    } else if (purchaseState === 1) {
      return 'canceled';
    } else if (purchaseState === 2) {
      return 'pending';
    }

    // Default to active if we can't determine
    return 'active';
  }

  /**
   * Get user-friendly error message
   */
  private getUserFriendlyErrorMessage(error: any): string {
    const code = error.code || '';

    // Common Google Play error codes
    if (code === 'E_USER_CANCELLED') {
      return 'Purchase was canceled';
    } else if (code === 'E_NETWORK_ERROR') {
      return 'Network error. Please check your connection and try again.';
    } else if (code === 'E_ITEM_UNAVAILABLE') {
      return 'This subscription is not available right now. Please try again later.';
    } else if (code === 'E_ALREADY_OWNED') {
      return 'You already have an active subscription';
    } else if (code === 'E_DEVELOPER_ERROR') {
      return 'Configuration error. Please contact support.';
    }

    return error.message || 'An error occurred during purchase';
  }

  /**
   * Check if error is recoverable (user can retry)
   */
  private isRecoverableError(error: any): boolean {
    const code = error.code || '';

    // Non-recoverable errors
    const nonRecoverable = [
      'E_DEVELOPER_ERROR',
      'E_ITEM_UNAVAILABLE',
      'E_ALREADY_OWNED',
    ];

    if (nonRecoverable.includes(code)) {
      return false;
    }

    // User canceled is technically recoverable (they can try again)
    // Network errors are recoverable
    return true;
  }
}


